import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useAuth, useTheme } from '../App';
import { useOrg } from '../contexts/OrgContext';
import { PageLayout } from '../components/Sidebar';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Switch } from '../components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Checkbox } from '../components/ui/checkbox';
import { ScrollArea } from '../components/ui/scroll-area';
import { toast } from 'sonner';
import {
  Calendar, Link2, Unlink, Building2, Users, Shield, Plus, Trash2,
  CheckCircle2, XCircle, RefreshCw, ArrowLeftRight, ArrowRight, Settings as SettingsIcon,
  Clock, CalendarDays, Loader2, Globe, Mail, MessageSquare, Phone, Plug, MessageCircle, AlertCircle, Zap, Eye, EyeOff,
  Palette, SlidersHorizontal, Sun, Moon, Monitor
} from 'lucide-react';
import LinkedInIntegrationCard from '../components/LinkedInIntegrationCard';
import VocabularySettingsPanel from '../components/settings/VocabularySettingsPanel';

const API = '/api';

// Common timezone list for both calendar and app settings
const TIMEZONE_OPTIONS = [
  { value: "UTC", label: "UTC (Coordinated Universal Time)" },
  // Americas
  { value: "America/New_York", label: "🇺🇸 New York (EST/EDT)" },
  { value: "America/Chicago", label: "🇺🇸 Chicago (CST/CDT)" },
  { value: "America/Denver", label: "🇺🇸 Denver (MST/MDT)" },
  { value: "America/Phoenix", label: "🇺🇸 Phoenix (MST)" },
  { value: "America/Los_Angeles", label: "🇺🇸 Los Angeles (PST/PDT)" },
  { value: "America/Anchorage", label: "🇺🇸 Anchorage (AKST/AKDT)" },
  { value: "Pacific/Honolulu", label: "🇺🇸 Honolulu (HST)" },
  { value: "America/Toronto", label: "🇨🇦 Toronto (EST/EDT)" },
  { value: "America/Vancouver", label: "🇨🇦 Vancouver (PST/PDT)" },
  { value: "America/Mexico_City", label: "🇲🇽 Mexico City (CST/CDT)" },
  { value: "America/Sao_Paulo", label: "🇧🇷 São Paulo (BRT)" },
  { value: "America/Buenos_Aires", label: "🇦🇷 Buenos Aires (ART)" },
  { value: "America/Lima", label: "🇵🇪 Lima (PET)" },
  { value: "America/Bogota", label: "🇨🇴 Bogotá (COT)" },
  { value: "America/Santiago", label: "🇨🇱 Santiago (CLT/CLST)" },
  // Europe
  { value: "Europe/London", label: "🇬🇧 London (GMT/BST)" },
  { value: "Europe/Dublin", label: "🇮🇪 Dublin (GMT/IST)" },
  { value: "Europe/Paris", label: "🇫🇷 Paris (CET/CEST)" },
  { value: "Europe/Berlin", label: "🇩🇪 Berlin (CET/CEST)" },
  { value: "Europe/Amsterdam", label: "🇳🇱 Amsterdam (CET/CEST)" },
  { value: "Europe/Brussels", label: "🇧🇪 Brussels (CET/CEST)" },
  { value: "Europe/Madrid", label: "🇪🇸 Madrid (CET/CEST)" },
  { value: "Europe/Rome", label: "🇮🇹 Rome (CET/CEST)" },
  { value: "Europe/Zurich", label: "🇨🇭 Zurich (CET/CEST)" },
  { value: "Europe/Vienna", label: "🇦🇹 Vienna (CET/CEST)" },
  { value: "Europe/Stockholm", label: "🇸🇪 Stockholm (CET/CEST)" },
  { value: "Europe/Oslo", label: "🇳🇴 Oslo (CET/CEST)" },
  { value: "Europe/Copenhagen", label: "🇩🇰 Copenhagen (CET/CEST)" },
  { value: "Europe/Helsinki", label: "🇫🇮 Helsinki (EET/EEST)" },
  { value: "Europe/Athens", label: "🇬🇷 Athens (EET/EEST)" },
  { value: "Europe/Warsaw", label: "🇵🇱 Warsaw (CET/CEST)" },
  { value: "Europe/Prague", label: "🇨🇿 Prague (CET/CEST)" },
  { value: "Europe/Budapest", label: "🇭🇺 Budapest (CET/CEST)" },
  { value: "Europe/Bucharest", label: "🇷🇴 Bucharest (EET/EEST)" },
  { value: "Europe/Moscow", label: "🇷🇺 Moscow (MSK)" },
  { value: "Europe/Kiev", label: "🇺🇦 Kyiv (EET/EEST)" },
  { value: "Europe/Istanbul", label: "🇹🇷 Istanbul (TRT)" },
  { value: "Europe/Lisbon", label: "🇵🇹 Lisbon (WET/WEST)" },
  // Middle East
  { value: "Asia/Dubai", label: "🇦🇪 Dubai (GST)" },
  { value: "Asia/Riyadh", label: "🇸🇦 Riyadh (AST)" },
  { value: "Asia/Jerusalem", label: "🇮🇱 Jerusalem (IST/IDT)" },
  { value: "Asia/Tehran", label: "🇮🇷 Tehran (IRST/IRDT)" },
  { value: "Asia/Kuwait", label: "🇰🇼 Kuwait (AST)" },
  { value: "Asia/Qatar", label: "🇶🇦 Doha (AST)" },
  { value: "Asia/Bahrain", label: "🇧🇭 Bahrain (AST)" },
  // Asia
  { value: "Asia/Kolkata", label: "🇮🇳 India (IST)" },
  { value: "Asia/Dhaka", label: "🇧🇩 Dhaka (BST)" },
  { value: "Asia/Karachi", label: "🇵🇰 Karachi (PKT)" },
  { value: "Asia/Bangkok", label: "🇹🇭 Bangkok (ICT)" },
  { value: "Asia/Ho_Chi_Minh", label: "🇻🇳 Ho Chi Minh (ICT)" },
  { value: "Asia/Jakarta", label: "🇮🇩 Jakarta (WIB)" },
  { value: "Asia/Singapore", label: "🇸🇬 Singapore (SGT)" },
  { value: "Asia/Kuala_Lumpur", label: "🇲🇾 Kuala Lumpur (MYT)" },
  { value: "Asia/Manila", label: "🇵🇭 Manila (PHT)" },
  { value: "Asia/Hong_Kong", label: "🇭🇰 Hong Kong (HKT)" },
  { value: "Asia/Shanghai", label: "🇨🇳 Shanghai (CST)" },
  { value: "Asia/Taipei", label: "🇹🇼 Taipei (CST)" },
  { value: "Asia/Seoul", label: "🇰🇷 Seoul (KST)" },
  { value: "Asia/Tokyo", label: "🇯🇵 Tokyo (JST)" },
  // Australia & Pacific
  { value: "Australia/Perth", label: "🇦🇺 Perth (AWST)" },
  { value: "Australia/Adelaide", label: "🇦🇺 Adelaide (ACST/ACDT)" },
  { value: "Australia/Darwin", label: "🇦🇺 Darwin (ACST)" },
  { value: "Australia/Brisbane", label: "🇦🇺 Brisbane (AEST)" },
  { value: "Australia/Sydney", label: "🇦🇺 Sydney (AEST/AEDT)" },
  { value: "Australia/Melbourne", label: "🇦🇺 Melbourne (AEST/AEDT)" },
  { value: "Australia/Hobart", label: "🇦🇺 Hobart (AEST/AEDT)" },
  { value: "Pacific/Auckland", label: "🇳🇿 Auckland (NZST/NZDT)" },
  { value: "Pacific/Fiji", label: "🇫🇯 Fiji (FJT)" },
  { value: "Pacific/Guam", label: "🇬🇺 Guam (ChST)" },
  // Africa
  { value: "Africa/Cairo", label: "🇪🇬 Cairo (EET)" },
  { value: "Africa/Johannesburg", label: "🇿🇦 Johannesburg (SAST)" },
  { value: "Africa/Lagos", label: "🇳🇬 Lagos (WAT)" },
  { value: "Africa/Nairobi", label: "🇰🇪 Nairobi (EAT)" },
  { value: "Africa/Casablanca", label: "🇲🇦 Casablanca (WET/WEST)" }
];

// WhatsApp Integration Card Component
const WhatsAppIntegrationCard = () => {
  const [status, setStatus] = useState(null);
  const [qrData, setQrData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [connecting, setConnecting] = useState(false);
  const [syncing, setSyncing] = useState(false);

  const API = '';

  const fetchStatus = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/whatsapp/status`, { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setStatus(data);
        
        // If awaiting scan, fetch QR code
        if (data.connection_status === 'awaiting_scan') {
          fetchQR();
        }
      }
    } catch (err) {
      console.error('WhatsApp status error:', err);
    } finally {
      setLoading(false);
    }
  }, [API]);

  const fetchQR = async () => {
    try {
      const res = await fetch(`${API}/api/whatsapp/qr`, { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setQrData(data);
      }
    } catch (err) {
      console.error('WhatsApp QR error:', err);
    }
  };

  const startConnection = async () => {
    setConnecting(true);
    try {
      // Reset the service to get a fresh QR
      await fetch(`${API}/api/whatsapp/reset`, {
        method: 'POST',
        credentials: 'include'
      });
      toast.info('Generating QR code... Please wait ~15 seconds');
      
      // Poll for QR code availability
      let attempts = 0;
      const pollQR = setInterval(async () => {
        attempts++;
        try {
          const statusRes = await fetch(`${API}/api/whatsapp/status`, { credentials: 'include' });
          if (statusRes.ok) {
            const statusData = await statusRes.json();
            setStatus(statusData);
            
            if (statusData.connection_status === 'awaiting_scan') {
              const qrRes = await fetch(`${API}/api/whatsapp/qr`, { credentials: 'include' });
              if (qrRes.ok) {
                const qr = await qrRes.json();
                if (qr.qr_data_url) {
                  setQrData(qr);
                  clearInterval(pollQR);
                  setConnecting(false);
                  return;
                }
              }
            } else if (statusData.connected) {
              clearInterval(pollQR);
              setConnecting(false);
              return;
            }
          }
        } catch(e) {}
        
        if (attempts > 30) {
          clearInterval(pollQR);
          setConnecting(false);
          toast.error('Timed out waiting for QR. Try again.');
        }
      }, 2000);
    } catch (err) {
      setConnecting(false);
      toast.error('Failed to connect: ' + err.message);
    }
  };

  const syncToSwitchboard = async () => {
    setSyncing(true);
    try {
      const res = await fetch(`${API}/api/whatsapp/sync`, {
        method: 'POST',
        credentials: 'include'
      });
      if (res.ok) {
        const result = await res.json();
        toast.success(`WhatsApp synced! ${result.sync_result?.chat_count || 0} chats found`);
        fetchStatus();
      } else {
        toast.error('Sync failed');
      }
    } catch (err) {
      toast.error('Sync failed');
    } finally {
      setSyncing(false);
    }
  };

  const activateChannel = async () => {
    try {
      const res = await fetch(`${API}/api/whatsapp/activate-channel`, {
        method: 'POST',
        credentials: 'include'
      });
      if (res.ok) {
        toast.success('WhatsApp channel activated in Switchboard!');
        fetchStatus();
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Activation failed');
      }
    } catch (err) {
      toast.error('Activation failed');
    }
  };

  const disconnect = async () => {
    try {
      const res = await fetch(`${API}/api/whatsapp/disconnect`, {
        method: 'POST',
        credentials: 'include'
      });
      if (res.ok) {
        toast.success('WhatsApp disconnected');
        setStatus(null);
        setQrData(null);
        fetchStatus();
      }
    } catch (err) {
      toast.error('Failed to disconnect');
    }
  };

  useEffect(() => {
    fetchStatus();
    
    // Poll for status updates and connection changes
    const interval = setInterval(async () => {
      const res = await fetch(`${API}/api/whatsapp/status`, { credentials: 'include' }).catch(() => null);
      if (res?.ok) {
        const data = await res.json();
        setStatus(data);
        
        // Auto-fetch QR when awaiting scan
        if (data.connection_status === 'awaiting_scan') {
          const qrRes = await fetch(`${API}/api/whatsapp/qr`, { credentials: 'include' }).catch(() => null);
          if (qrRes?.ok) {
            const qr = await qrRes.json();
            if (qr.qr_data_url) setQrData(qr);
          }
        }
      }
    }, 4000);

    return () => clearInterval(interval);
  }, [fetchStatus, API]);

  return (
    <Card className="flex flex-col" data-testid="whatsapp-integration-card">
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-3 text-base">
          <div className="w-10 h-10 rounded-xl bg-white dark:bg-slate-100 border dark:border-slate-300 flex items-center justify-center flex-shrink-0">
            <img src="https://cdn.simpleicons.org/whatsapp" alt="WhatsApp" className="w-6 h-6" />
          </div>
          <div className="flex-1">
            <div className="flex items-center gap-2">
              <span>WhatsApp</span>
              {status?.connected && <Badge variant="default" className="text-[10px] bg-green-500">Connected</Badge>}
            </div>
            <p className="text-xs text-muted-foreground font-normal mt-0.5">Personal messaging sync</p>
          </div>
        </CardTitle>
      </CardHeader>
      <CardContent className="pt-0 flex-1 flex flex-col justify-end">
        {loading ? (
          <div className="flex items-center justify-center py-4">
            <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
          </div>
        ) : status?.connected ? (
          <div className="space-y-2">
            <div className="flex items-center justify-between p-2 bg-green-50 dark:bg-green-950/30 rounded-lg border border-green-200 dark:border-green-900">
              <div className="flex items-center gap-2">
                <div className="w-6 h-6 rounded-full bg-green-100 dark:bg-green-950/40 flex items-center justify-center">
                  <MessageCircle className="h-3 w-3 text-green-600 dark:text-green-400" />
                </div>
                <span className="text-sm font-medium truncate">{status.user?.name || 'WhatsApp'}</span>
              </div>
              <div className="flex items-center gap-1">
                <Button 
                  variant="ghost" 
                  size="icon"
                  className="h-7 w-7"
                  onClick={syncToSwitchboard}
                  disabled={syncing}
                  data-testid="sync-whatsapp-btn"
                >
                  {syncing ? (
                    <Loader2 className="h-3 w-3 animate-spin" />
                  ) : (
                    <RefreshCw className="h-3 w-3" />
                  )}
                </Button>
                <Button variant="ghost" size="icon" className="h-7 w-7 text-red-500" onClick={disconnect}>
                  <Unlink className="h-3 w-3" />
                </Button>
              </div>
            </div>
            <Button variant="outline" size="sm" className="w-full" onClick={activateChannel}>
              <Plug className="h-3 w-3 mr-1" />Activate Channel
            </Button>
          </div>
        ) : status?.connection_status === 'awaiting_scan' && qrData?.qr_data_url ? (
          <div className="flex flex-col items-center py-2">
            <div className="bg-white p-2 rounded-lg border dark:border-slate-600 shadow-sm mb-2">
              <img 
                src={qrData.qr_data_url} 
                alt="WhatsApp QR Code" 
                className="w-32 h-32"
                data-testid="whatsapp-qr-code"
              />
            </div>
            <p className="text-xs text-muted-foreground text-center">
              Scan with WhatsApp to connect
            </p>
          </div>
        ) : (
          <div className="flex flex-col items-center py-4 text-center">
            <Button size="sm" onClick={startConnection} disabled={connecting} data-testid="connect-whatsapp">
              {connecting ? (
                <><Loader2 className="h-3 w-3 mr-1 animate-spin" />Connecting...</>
              ) : (
                <><Link2 className="h-3 w-3 mr-1" />Connect</>
              )}
            </Button>
          </div>
        )}
        {/* Purge always visible at bottom regardless of connection state */}
        <Button 
          variant="ghost" 
          size="sm"
          className="w-full mt-2 text-red-500 hover:text-red-700 hover:bg-red-50 dark:text-red-400 dark:hover:text-red-300 dark:hover:bg-red-950/40 text-xs"
          data-testid="purge-whatsapp-btn"
          onClick={async () => {
            if (!window.confirm('Purge all WhatsApp messages and threads? You can re-sync after.')) return;
            try {
              const res = await fetch(`${API}/api/switchboard/purge-provider/whatsapp`, { method: 'POST', credentials: 'include' });
              if (res.ok) {
                const result = await res.json();
                toast.success(`Purged ${result.deleted_messages} messages, ${result.deleted_threads} threads`);
              } else {
                const err = await res.json().catch(() => ({}));
                toast.error(`WhatsApp purge failed: ${err.detail || res.statusText}`);
              }
            } catch(e) { toast.error(`WhatsApp purge error: ${e.message}`); }
          }}
        >
          <Trash2 className="h-3 w-3 mr-1" />Purge Data
        </Button>
      </CardContent>
    </Card>
  );
};

// Google Drive (Drive + Docs + Sheets + Slides) Integration Card
const GoogleDriveIntegrationCard = () => {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [connecting, setConnecting] = useState(false);
  const [testing, setTesting] = useState(false);
  const [searchParams] = useSearchParams();

  const fetchStatus = useCallback(async () => {
    try {
      const res = await fetch(`/api/oauth/drive/status`, { credentials: 'include' });
      if (res.ok) setStatus(await res.json());
    } catch (e) { console.error('Drive status error:', e); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchStatus(); }, [fetchStatus]);

  useEffect(() => {
    if (searchParams.get('drive_connected') === 'true') {
      toast.success('Google Drive connected');
      const url = new URL(window.location.href);
      url.searchParams.delete('drive_connected');
      window.history.replaceState({}, '', url.toString());
      fetchStatus();
    }
    const err = searchParams.get('drive_error');
    if (err) {
      toast.error(`Drive connect failed: ${err}`);
      const url = new URL(window.location.href);
      url.searchParams.delete('drive_error');
      window.history.replaceState({}, '', url.toString());
    }
  }, [searchParams, fetchStatus]);

  const connect = async () => {
    setConnecting(true);
    try {
      const res = await fetch(`/api/oauth/drive/connect`, { credentials: 'include' });
      if (!res.ok) {
        const e = await res.json().catch(() => ({}));
        toast.error(e.detail || 'Failed to start Drive flow');
        setConnecting(false);
        return;
      }
      const { authorization_url } = await res.json();
      window.location.href = authorization_url;
    } catch (e) {
      toast.error('Drive connect error');
      setConnecting(false);
    }
  };

  const disconnect = async () => {
    if (!window.confirm('Disconnect Google Drive? Orka will lose access to Drive, Docs, Sheets and Slides.')) return;
    try {
      const res = await fetch(`/api/oauth/drive/disconnect`, {
        method: 'POST', credentials: 'include',
      });
      if (res.ok) {
        toast.success('Google Drive disconnected');
        setStatus({ connected: false });
      } else { toast.error('Disconnect failed'); }
    } catch (e) { toast.error('Disconnect error'); }
  };

  const testConnection = async () => {
    setTesting(true);
    try {
      const res = await fetch(`/api/oauth/drive/test`, { method: 'POST', credentials: 'include' });
      const data = await res.json();
      if (data.success) {
        toast.success(`Drive reachable — saw ${data.files_seen} files`);
      } else {
        toast.error(`Test failed: ${data.error || 'unknown'}`);
      }
    } catch (e) { toast.error('Test failed'); }
    finally { setTesting(false); }
  };

  const userEmail = status?.connection?.user_email;

  return (
    <Card className="flex flex-col" data-testid="google-drive-integration-card">
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-3 text-base">
          <div className="w-10 h-10 rounded-xl bg-white dark:bg-slate-100 border dark:border-slate-300 flex items-center justify-center flex-shrink-0">
            <img src="https://cdn.simpleicons.org/googledrive/4285F4" alt="Google Drive" className="w-6 h-6" />
          </div>
          <div className="flex-1">
            <div className="flex items-center gap-2">
              <span>Google Drive</span>
              {status?.connected && <Badge variant="default" className="text-[10px] bg-blue-500">Connected</Badge>}
            </div>
            <p className="text-xs text-muted-foreground font-normal mt-0.5">Drive + Docs + Sheets + Slides</p>
          </div>
        </CardTitle>
      </CardHeader>
      <CardContent className="pt-0 flex-1 flex flex-col justify-end">
        {loading ? (
          <div className="flex items-center justify-center py-4">
            <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
          </div>
        ) : status?.connected ? (
          <div className="space-y-2">
            <div className="flex items-center justify-between p-2 bg-blue-50 dark:bg-blue-950/30 rounded-lg border border-blue-200 dark:border-blue-900">
              <div className="flex items-center gap-2 min-w-0">
                <div className="w-6 h-6 rounded-full bg-blue-100 dark:bg-blue-950/40 flex items-center justify-center flex-shrink-0">
                  <Plug className="h-3 w-3 text-blue-600 dark:text-blue-400" />
                </div>
                <span className="text-sm font-medium truncate" title={userEmail}>
                  {userEmail || 'Connected'}
                </span>
              </div>
              <div className="flex items-center gap-1 flex-shrink-0">
                <Button
                  variant="ghost" size="icon" className="h-7 w-7"
                  onClick={testConnection} disabled={testing}
                  data-testid="gdrive-test-btn" title="Test connection"
                >
                  {testing ? <Loader2 className="h-3 w-3 animate-spin" /> : <RefreshCw className="h-3 w-3" />}
                </Button>
                <Button
                  variant="ghost" size="icon"
                  className="h-7 w-7 text-red-500 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300"
                  onClick={disconnect}
                  data-testid="gdrive-disconnect-btn" title="Disconnect"
                >
                  <Unlink className="h-3 w-3" />
                </Button>
              </div>
            </div>
            <p className="text-[11px] text-muted-foreground">
              {status?.connection?.scopes_count} scopes granted
            </p>
          </div>
        ) : (
          <div className="flex flex-col items-center py-4 text-center">
            <Button
              size="sm" onClick={connect} disabled={connecting}
              data-testid="connect-gdrive-btn"
            >
              {connecting ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <Link2 className="h-3 w-3 mr-1" />}
              Connect
            </Button>
            <p className="text-[10px] text-muted-foreground mt-2">
              Read & write Drive files, Google Docs, Sheets, Slides
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

// YouTube Integration Card
const YouTubeIntegrationCard = () => {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [connecting, setConnecting] = useState(false);
  const [testing, setTesting] = useState(false);
  const [searchParams] = useSearchParams();

  const fetchStatus = useCallback(async () => {
    try {
      const res = await fetch(`/api/oauth/youtube/status`, { credentials: 'include' });
      if (res.ok) setStatus(await res.json());
    } catch (e) { console.error('YouTube status error:', e); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchStatus(); }, [fetchStatus]);

  useEffect(() => {
    if (searchParams.get('youtube_connected') === 'true') {
      toast.success('YouTube connected');
      const url = new URL(window.location.href);
      url.searchParams.delete('youtube_connected');
      window.history.replaceState({}, '', url.toString());
      fetchStatus();
    }
    const err = searchParams.get('youtube_error');
    if (err) {
      toast.error(`YouTube connect failed: ${err}`);
      const url = new URL(window.location.href);
      url.searchParams.delete('youtube_error');
      window.history.replaceState({}, '', url.toString());
    }
  }, [searchParams, fetchStatus]);

  const connect = async () => {
    setConnecting(true);
    try {
      const res = await fetch(`/api/oauth/youtube/connect`, { credentials: 'include' });
      if (!res.ok) {
        const e = await res.json().catch(() => ({}));
        toast.error(e.detail || 'Failed to start YouTube flow');
        setConnecting(false);
        return;
      }
      const { authorization_url } = await res.json();
      window.location.href = authorization_url;
    } catch (e) {
      toast.error('YouTube connect error');
      setConnecting(false);
    }
  };

  const disconnect = async () => {
    if (!window.confirm('Disconnect YouTube? Orka will lose access to your YouTube data.')) return;
    try {
      const res = await fetch(`/api/oauth/youtube/disconnect`, {
        method: 'POST', credentials: 'include',
      });
      if (res.ok) {
        toast.success('YouTube disconnected');
        setStatus({ connected: false });
      } else { toast.error('Disconnect failed'); }
    } catch (e) { toast.error('Disconnect error'); }
  };

  const testConnection = async () => {
    setTesting(true);
    try {
      const res = await fetch(`/api/oauth/youtube/test`, { method: 'POST', credentials: 'include' });
      const data = await res.json();
      if (data.success) {
        const ch = data.info?.channel_title || data.info?.email || 'user';
        toast.success(`YouTube reachable — signed in as ${ch}`);
      } else {
        toast.error(`Test failed: ${data.error || 'unknown'}`);
      }
    } catch (e) { toast.error('Test failed'); }
    finally { setTesting(false); }
  };

  const channelTitle = status?.connection?.channel_title;
  const userEmail = status?.connection?.user_email;

  return (
    <Card className="flex flex-col" data-testid="youtube-integration-card">
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-3 text-base">
          <div className="w-10 h-10 rounded-xl bg-white dark:bg-slate-100 border dark:border-slate-300 flex items-center justify-center flex-shrink-0">
            <img src="https://cdn.simpleicons.org/youtube/FF0000" alt="YouTube" className="w-6 h-6" />
          </div>
          <div className="flex-1">
            <div className="flex items-center gap-2">
              <span>YouTube</span>
              {status?.connected && <Badge variant="default" className="text-[10px] bg-red-500">Connected</Badge>}
            </div>
            <p className="text-xs text-muted-foreground font-normal mt-0.5">Search, transcripts, subscriptions, playlists</p>
          </div>
        </CardTitle>
      </CardHeader>
      <CardContent className="pt-0 flex-1 flex flex-col justify-end">
        {loading ? (
          <div className="flex items-center justify-center py-4">
            <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
          </div>
        ) : status?.connected ? (
          <div className="space-y-2">
            <div className="flex items-center justify-between p-2 bg-red-50 dark:bg-red-950/30 rounded-lg border border-red-200 dark:border-red-900">
              <div className="flex items-center gap-2 min-w-0">
                <div className="w-6 h-6 rounded-full bg-red-100 dark:bg-red-950/40 flex items-center justify-center flex-shrink-0">
                  <Plug className="h-3 w-3 text-red-600 dark:text-red-400" />
                </div>
                <span className="text-sm font-medium truncate" title={channelTitle || userEmail}>
                  {channelTitle || userEmail || 'Connected'}
                </span>
              </div>
              <div className="flex items-center gap-1 flex-shrink-0">
                <Button
                  variant="ghost" size="icon" className="h-7 w-7"
                  onClick={testConnection} disabled={testing}
                  data-testid="youtube-test-btn" title="Test connection"
                >
                  {testing ? <Loader2 className="h-3 w-3 animate-spin" /> : <RefreshCw className="h-3 w-3" />}
                </Button>
                <Button
                  variant="ghost" size="icon"
                  className="h-7 w-7 text-red-500 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300"
                  onClick={disconnect}
                  data-testid="youtube-disconnect-btn" title="Disconnect"
                >
                  <Unlink className="h-3 w-3" />
                </Button>
              </div>
            </div>
            <p className="text-[11px] text-muted-foreground">
              {status?.connection?.scopes_count} scopes granted
            </p>
          </div>
        ) : (
          <div className="flex flex-col items-center py-4 text-center">
            <Button
              size="sm" onClick={connect} disabled={connecting}
              data-testid="connect-youtube-btn"
            >
              {connecting ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <Link2 className="h-3 w-3 mr-1" />}
              Connect
            </Button>
            <p className="text-[10px] text-muted-foreground mt-2">
              Search, transcripts, subscriptions, playlists, ratings
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

// Atlassian (Confluence + Jira) Integration Card
const AtlassianIntegrationCard = () => {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [connecting, setConnecting] = useState(false);
  const [testing, setTesting] = useState(false);
  const [searchParams] = useSearchParams();

  const API = '';

  const fetchStatus = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/integrations/atlassian/status`, { credentials: 'include' });
      if (res.ok) setStatus(await res.json());
    } catch (err) {
      console.error('Atlassian status error:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchStatus();
  }, [fetchStatus]);

  // Surface query-string callback results from the OAuth redirect.
  useEffect(() => {
    if (searchParams.get('atlassian_connected') === 'true') {
      toast.success('Atlassian connected');
      // Strip the param so refreshes don't replay the toast.
      const url = new URL(window.location.href);
      url.searchParams.delete('atlassian_connected');
      window.history.replaceState({}, '', url.toString());
      fetchStatus();
    }
    const err = searchParams.get('atlassian_error');
    if (err) {
      toast.error(`Atlassian connect failed: ${err}`);
      const url = new URL(window.location.href);
      url.searchParams.delete('atlassian_error');
      window.history.replaceState({}, '', url.toString());
    }
  }, [searchParams, fetchStatus]);

  const connect = async () => {
    setConnecting(true);
    try {
      const res = await fetch(`${API}/api/integrations/atlassian/connect`, { credentials: 'include' });
      if (!res.ok) {
        const e = await res.json().catch(() => ({}));
        toast.error(e.detail || 'Failed to start Atlassian flow');
        setConnecting(false);
        return;
      }
      const { authorization_url } = await res.json();
      window.location.href = authorization_url;
    } catch (e) {
      toast.error('Atlassian connect error');
      setConnecting(false);
    }
  };

  const disconnect = async () => {
    if (!window.confirm('Disconnect Atlassian? Orka will lose access to Jira & Confluence.')) return;
    try {
      const res = await fetch(`${API}/api/integrations/atlassian/disconnect`, {
        method: 'POST', credentials: 'include',
      });
      if (res.ok) {
        toast.success('Atlassian disconnected');
        setStatus({ connected: false });
      } else {
        toast.error('Disconnect failed');
      }
    } catch (e) { toast.error('Disconnect error'); }
  };

  const testConnection = async () => {
    setTesting(true);
    try {
      const res = await fetch(`${API}/api/integrations/atlassian/test`, {
        method: 'POST', credentials: 'include',
      });
      const data = await res.json();
      if (data.success) {
        toast.success(`Jira reachable — ${data.projects_count} projects visible`);
      } else {
        toast.error(`Test failed: ${data.error || 'unknown'}`);
      }
    } catch (e) { toast.error('Test failed'); }
    finally { setTesting(false); }
  };

  const sites = status?.connection?.sites || [];
  const defaultSiteUrl = status?.connection?.default_site_url;

  return (
    <Card className="flex flex-col" data-testid="atlassian-integration-card">
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-3 text-base">
          <div className="w-10 h-10 rounded-xl bg-white dark:bg-slate-100 border dark:border-slate-300 flex items-center justify-center flex-shrink-0">
            <img src="https://cdn.simpleicons.org/atlassian/0052CC" alt="Atlassian" className="w-6 h-6" />
          </div>
          <div className="flex-1">
            <div className="flex items-center gap-2">
              <span>Atlassian</span>
              {status?.connected && <Badge variant="default" className="text-[10px] bg-blue-600">Connected</Badge>}
            </div>
            <p className="text-xs text-muted-foreground font-normal mt-0.5">Confluence + Jira (read & write)</p>
          </div>
        </CardTitle>
      </CardHeader>
      <CardContent className="pt-0 flex-1 flex flex-col justify-end">
        {loading ? (
          <div className="flex items-center justify-center py-4">
            <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
          </div>
        ) : status?.connected ? (
          <div className="space-y-2">
            <div className="flex items-center justify-between p-2 bg-blue-50 dark:bg-blue-950/30 rounded-lg border border-blue-200 dark:border-blue-900">
              <div className="flex items-center gap-2 min-w-0">
                <div className="w-6 h-6 rounded-full bg-blue-100 dark:bg-blue-950/40 flex items-center justify-center flex-shrink-0">
                  <Plug className="h-3 w-3 text-blue-600 dark:text-blue-400" />
                </div>
                <a
                  href={defaultSiteUrl || '#'}
                  target="_blank"
                  rel="noreferrer"
                  className="text-sm font-medium truncate hover:underline"
                  data-testid="atlassian-site-link"
                  title={defaultSiteUrl}
                >
                  {defaultSiteUrl ? defaultSiteUrl.replace(/^https?:\/\//, '') : 'Connected'}
                </a>
              </div>
              <div className="flex items-center gap-1 flex-shrink-0">
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-7 w-7"
                  onClick={testConnection}
                  disabled={testing}
                  data-testid="atlassian-test-btn"
                  title="Test connection"
                >
                  {testing ? <Loader2 className="h-3 w-3 animate-spin" /> : <RefreshCw className="h-3 w-3" />}
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-7 w-7 text-red-500 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300"
                  onClick={disconnect}
                  data-testid="atlassian-disconnect-btn"
                  title="Disconnect"
                >
                  <Unlink className="h-3 w-3" />
                </Button>
              </div>
            </div>
            {sites.length > 1 && (
              <p className="text-[11px] text-muted-foreground">
                {sites.length} sites accessible
              </p>
            )}
          </div>
        ) : (
          <div className="flex flex-col items-center py-4 text-center">
            <Button
              size="sm"
              onClick={connect}
              disabled={connecting}
              data-testid="connect-atlassian-btn"
            >
              {connecting ? (
                <Loader2 className="h-3 w-3 mr-1 animate-spin" />
              ) : (
                <Link2 className="h-3 w-3 mr-1" />
              )}
              Connect
            </Button>
            <p className="text-[10px] text-muted-foreground mt-2">
              Connects via OAuth 2.0 (3LO) — read & write Jira issues + Confluence pages
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

// Slack Integration Card Component
const SlackIntegrationCard = () => {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [connecting, setConnecting] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [showTokenInput, setShowTokenInput] = useState(false);
  const [botToken, setBotToken] = useState('');

  const API = '';

  const fetchStatus = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/slack/status`, { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setStatus(data);
      }
    } catch (err) {
      console.error('Slack status error:', err);
    } finally {
      setLoading(false);
    }
  }, [API]);

  const connectSlack = async () => {
    if (!botToken.trim()) {
      toast.error('Please enter your Bot User OAuth Token');
      return;
    }
    
    setConnecting(true);
    try {
      const res = await fetch(`${API}/api/slack/connect`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ bot_token: botToken })
      });
      
      if (res.ok) {
        const result = await res.json();
        toast.success(`Connected to ${result.team_name}!`);
        setBotToken('');
        setShowTokenInput(false);
        fetchStatus();
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Connection failed');
      }
    } catch (err) {
      toast.error('Connection failed');
    } finally {
      setConnecting(false);
    }
  };

  const syncSlack = async () => {
    setSyncing(true);
    try {
      const res = await fetch(`${API}/api/slack/sync`, {
        method: 'POST',
        credentials: 'include'
      });
      
      if (res.ok) {
        const result = await res.json();
        toast.success(`Synced ${result.synced_count || 0} messages from Slack`);
      } else {
        toast.error('Sync failed');
      }
    } catch (err) {
      toast.error('Sync failed');
    } finally {
      setSyncing(false);
    }
  };

  const disconnect = async () => {
    try {
      const res = await fetch(`${API}/api/slack/disconnect`, {
        method: 'POST',
        credentials: 'include'
      });
      if (res.ok) {
        toast.success('Slack disconnected');
        setStatus(null);
        fetchStatus();
      }
    } catch (err) {
      toast.error('Disconnect failed');
    }
  };

  useEffect(() => {
    fetchStatus();
  }, [fetchStatus]);

  return (
    <Card className="flex flex-col" data-testid="slack-integration-card">
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-3 text-base">
          <div className="w-10 h-10 rounded-xl bg-white dark:bg-slate-100 border dark:border-slate-300 flex items-center justify-center flex-shrink-0">
            <img src="https://cdn.simpleicons.org/slack" alt="Slack" className="w-6 h-6" />
          </div>
          <div className="flex-1">
            <div className="flex items-center gap-2">
              <span>Slack</span>
              {status?.connected && <Badge variant="default" className="text-[10px] bg-purple-500">Connected</Badge>}
            </div>
            <p className="text-xs text-muted-foreground font-normal mt-0.5">Workspace messaging sync</p>
          </div>
        </CardTitle>
      </CardHeader>
      <CardContent className="pt-0 flex-1 flex flex-col justify-end">
        {loading ? (
          <div className="flex items-center justify-center py-4">
            <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
          </div>
        ) : status?.connected ? (
          <div className="space-y-2">
            <div className="flex items-center justify-between p-2 bg-purple-50 dark:bg-purple-950/30 rounded-lg border border-purple-200 dark:border-purple-900">
              <div className="flex items-center gap-2">
                <div className="w-6 h-6 rounded-full bg-purple-100 dark:bg-purple-950/40 flex items-center justify-center">
                  <MessageSquare className="h-3 w-3 text-purple-600 dark:text-purple-400" />
                </div>
                <span className="text-sm font-medium truncate">{status.team_name || 'Slack'}</span>
              </div>
              <div className="flex items-center gap-1">
                <Button 
                  variant="ghost" 
                  size="icon"
                  className="h-7 w-7"
                  onClick={syncSlack}
                  disabled={syncing}
                  data-testid="sync-slack-btn"
                >
                  {syncing ? (
                    <Loader2 className="h-3 w-3 animate-spin" />
                  ) : (
                    <RefreshCw className="h-3 w-3" />
                  )}
                </Button>
                <Button variant="ghost" size="icon" className="h-7 w-7 text-red-500" onClick={disconnect}>
                  <Unlink className="h-3 w-3" />
                </Button>
              </div>
            </div>
          </div>
        ) : showTokenInput ? (
          <div className="space-y-2">
            <Input
              type="password"
              value={botToken}
              onChange={(e) => setBotToken(e.target.value)}
              placeholder="xoxb-your-token"
              className="font-mono text-xs h-8"
            />
            <div className="flex gap-1">
              <Button size="sm" onClick={connectSlack} disabled={connecting || !botToken.trim()}>
                {connecting ? (
                  <Loader2 className="h-3 w-3 animate-spin" />
                ) : (
                  'Connect'
                )}
              </Button>
              <Button size="sm" variant="ghost" onClick={() => setShowTokenInput(false)}>
                Cancel
              </Button>
            </div>
          </div>
        ) : (
          <div className="flex flex-col items-center py-4 text-center">
            <Button size="sm" onClick={() => setShowTokenInput(true)} data-testid="connect-slack">
              <Link2 className="h-3 w-3 mr-1" />Connect
            </Button>
            <p className="text-[10px] text-muted-foreground mt-2">
              Requires Slack App with bot token
            </p>
          </div>
        )}
        {/* Purge always visible at bottom regardless of connection state */}
        <Button 
          variant="ghost" 
          size="sm"
          className="w-full mt-2 text-red-500 hover:text-red-700 hover:bg-red-50 dark:text-red-400 dark:hover:text-red-300 dark:hover:bg-red-950/40 text-xs"
          data-testid="purge-slack-btn"
          onClick={async () => {
            if (!window.confirm('Purge all Slack messages and threads? You can re-sync after.')) return;
            try {
              const res = await fetch(`${API}/api/switchboard/purge-provider/slack`, { method: 'POST', credentials: 'include' });
              if (res.ok) {
                const result = await res.json();
                toast.success(`Purged ${result.deleted_messages} messages, ${result.deleted_threads} threads`);
              } else {
                const err = await res.json().catch(() => ({}));
                toast.error(`Slack purge failed: ${err.detail || res.statusText}`);
              }
            } catch(e) { toast.error(`Slack purge error: ${e.message}`); }
          }}
        >
          <Trash2 className="h-3 w-3 mr-1" />Purge Data
        </Button>
      </CardContent>
    </Card>
  );
};


// Google Workspace Integration Tile for Settings
const WorkspaceIntegrationTile = () => {
  const [status, setStatus] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    fetch(`${API}/org/workspace/status`, { credentials: 'include' })
      .then(r => r.ok ? r.json() : null)
      .then(d => d && setStatus(d))
      .catch(() => {});
  }, []);

  const handleConnect = async () => {
    try {
      const res = await fetch(`${API}/org/workspace/connect`, { credentials: 'include' });
      if (res.ok) {
        const { auth_url } = await res.json();
        window.location.href = auth_url;
      } else {
        const err = await res.json().catch(() => ({}));
        const detail = err.detail || '';
        if (res.status === 403 && detail.toLowerCase().includes('no active organisation')) {
          toast.error('Create an organisation first (Organisation page)');
        } else {
          toast.error(detail || 'Failed to start connection');
        }
      }
    } catch { toast.error('Connection error'); }
  };

  const handleDisconnect = async () => {
    if (!window.confirm('Disconnect Google Workspace?')) return;
    try {
      await fetch(`${API}/org/workspace/disconnect`, { method: 'POST', credentials: 'include' });
      toast.success('Disconnected');
      setStatus({ connected: false });
    } catch { toast.error('Failed'); }
  };

  if (status?.connected) {
    return (
      <div className="space-y-2">
        <div className="flex items-center gap-2">
          <span className="inline-block w-2 h-2 rounded-full bg-emerald-500" />
          <span className="text-sm text-emerald-700 dark:text-emerald-400 font-medium">Connected</span>
        </div>
        <p className="text-xs orka-text-tertiary">Domain: {status.domain}</p>
        <div className="flex gap-2">
          <Button size="sm" variant="outline" onClick={() => navigate('/organisation')}>Manage Users</Button>
          <Button size="sm" variant="ghost" className="text-red-600 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300" onClick={handleDisconnect}>Disconnect</Button>
        </div>
      </div>
    );
  }

  return (
    <Button size="sm" onClick={handleConnect} data-testid="connect-workspace-btn">Connect Google Workspace</Button>
  );
};


const Settings = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, logout } = useAuth();
  const { theme, setTheme, density, setDensity } = useTheme();
  const { orgId, handle409 } = useOrg();

  const [calendarStatus, setCalendarStatus] = useState(null);
  const [calendarList, setCalendarList] = useState([]);
  const [calendarSettings, setCalendarSettings] = useState(null);
  const [profileSettings, setProfileSettings] = useState(null);
  const [organization, setOrganization] = useState(null);
  const [members, setMembers] = useState([]);
  const [delegations, setDelegations] = useState({ as_supporter: [], as_principal: [] });
  const [loading, setLoading] = useState(true);
  
  // Gmail integration state
  const [gmailStatus, setGmailStatus] = useState(null);
  const [gmailAccounts, setGmailAccounts] = useState([]);
  const [gmailSyncing, setGmailSyncing] = useState(false);
  
  // OpenAI state
  const [openaiStatus, setOpenaiStatus] = useState(null);
  const [openaiKey, setOpenaiKey] = useState('');
  const [openaiSaving, setOpenaiSaving] = useState(false);
  const [showOpenaiInput, setShowOpenaiInput] = useState(false);
  
  const [showCreateOrg, setShowCreateOrg] = useState(false);
  const [showCreateDelegation, setShowCreateDelegation] = useState(false);
  const [showCalendarSettings, setShowCalendarSettings] = useState(false);
  const [showSyncDialog, setShowSyncDialog] = useState(false);
  const [orgName, setOrgName] = useState('');
  const [delegationEmail, setDelegationEmail] = useState('');
  const [delegationProfileIds, setDelegationProfileIds] = useState([]);
  const [accessProfiles, setAccessProfiles] = useState([]);
  const [savingCalSettings, setSavingCalSettings] = useState(false);
  const [savingProfileSettings, setSavingProfileSettings] = useState(false);
  const [fetchingCalendarTz, setFetchingCalendarTz] = useState(false);
  
  // Sync state
  const [syncMode, setSyncMode] = useState('days'); // 'days' or 'dates'
  const [syncDaysBack, setSyncDaysBack] = useState('0');
  const [syncDaysForward, setSyncDaysForward] = useState('90');
  const [syncStartDate, setSyncStartDate] = useState('');
  const [syncEndDate, setSyncEndDate] = useState('');
  const [syncing, setSyncing] = useState(false);
  const [syncResult, setSyncResult] = useState(null);

  // Epic 7 — sync health panel state
  const [syncLog, setSyncLog] = useState(null);
  const [loadingSyncLog, setLoadingSyncLog] = useState(false);
  const [forcingFullSync, setForcingFullSync] = useState(false);

  const fetchSyncLog = useCallback(async () => {
    setLoadingSyncLog(true);
    try {
      const res = await fetch(`${API}/oauth/calendar/sync-log?limit=50`, { credentials: 'include' });
      if (res.ok) {
        setSyncLog(await res.json());
      }
    } catch (e) {
      // best-effort — don't toast noise here
    } finally {
      setLoadingSyncLog(false);
    }
  }, []);

  const handleForceFullSync = async () => {
    setForcingFullSync(true);
    try {
      const res = await fetch(`${API}/oauth/calendar/sync`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ force_full: true }),
      });
      if (res.ok) {
        const data = await res.json();
        toast.success(`Full resync: ${data.synced_count || 0} new, ${data.updated_count || 0} updated, ${data.cancelled_count || 0} cancelled`);
        fetchSyncLog();
      } else {
        toast.error('Full resync failed');
      }
    } catch (e) {
      toast.error('Full resync failed');
    } finally {
      setForcingFullSync(false);
    }
  };

  useEffect(() => {
    if (showCalendarSettings) fetchSyncLog();
  }, [showCalendarSettings, fetchSyncLog]);

  // Check callback params
  useEffect(() => {
    if (searchParams.get('calendar_connected') === 'true') {
      toast.success('Google Calendar connected!');
      navigate('/settings', { replace: true });
    }
    if (searchParams.get('calendar_error')) {
      toast.error('Failed to connect calendar');
      navigate('/settings', { replace: true });
    }
    if (searchParams.get('gmail_success') === 'true') {
      toast.success('Gmail connected successfully!');
      navigate('/settings?tab=integrations', { replace: true });
    }
    if (searchParams.get('gmail_error')) {
      toast.error(`Gmail connection failed: ${searchParams.get('gmail_error')}`);
      navigate('/settings?tab=integrations', { replace: true });
    }
  }, [searchParams, navigate]);

  // Fetch data
  const fetchData = useCallback(async () => {
    if (!user) return;
    setLoading(true);
    try {
      const [calRes, orgRes, memRes, delRes, profileRes, gmailRes, gmailAccountsRes, openaiRes] = await Promise.all([
        fetch(`${API}/oauth/calendar/status`, { credentials: 'include' }),
        fetch(`${API}/org/current${orgId ? `?org_id=${orgId}` : ''}`, { credentials: 'include' }),
        fetch(`${API}/org/members`, { credentials: 'include' }),
        fetch(`${API}/org/delegations`, { credentials: 'include' }),
        fetch(`${API}/auth/profile/settings`, { credentials: 'include' }),
        fetch(`${API}/oauth/gmail/status`, { credentials: 'include' }),
        fetch(`${API}/oauth/gmail/accounts`, { credentials: 'include' }),
        fetch(`${API}/ai/openai-key-status`, { credentials: 'include' })
      ]);
      if (calRes.ok) setCalendarStatus(await calRes.json());
      if (orgRes.status === 409) { handle409(); }
      else if (orgRes.ok) setOrganization(await orgRes.json());
      if (memRes.ok) setMembers(await memRes.json());
      if (delRes.ok) setDelegations(await delRes.json());
      if (profileRes.ok) setProfileSettings(await profileRes.json());
      if (gmailRes.ok) setGmailStatus(await gmailRes.json());
      if (gmailAccountsRes.ok) {
        const data = await gmailAccountsRes.json();
        setGmailAccounts(data.accounts || []);
      }
      if (openaiRes.ok) setOpenaiStatus(await openaiRes.json());
      // Fetch access profiles for delegation UI
      try {
        const profilesRes = await fetch(`${API}/org/access/profiles`, { credentials: 'include' });
        if (profilesRes.ok) setAccessProfiles(await profilesRes.json());
      } catch {}
    } catch (error) {
      console.error('Failed to fetch settings data:', error);
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Fetch calendar list and settings when connected
  const fetchCalendarDetails = useCallback(async () => {
    if (!calendarStatus?.connected) return;
    try {
      const [listRes, settingsRes] = await Promise.all([
        fetch(`${API}/oauth/calendar/list`, { credentials: 'include' }),
        fetch(`${API}/oauth/calendar/settings`, { credentials: 'include' })
      ]);
      if (listRes.ok) setCalendarList((await listRes.json()).calendars || []);
      if (settingsRes.ok) setCalendarSettings(await settingsRes.json());
    } catch (error) {
      console.error('Failed to fetch calendar details:', error);
    }
  }, [calendarStatus?.connected]);

  useEffect(() => { fetchCalendarDetails(); }, [fetchCalendarDetails]);

  // Fetch calendar timezone from Google Calendar API
  const fetchCalendarTimezone = async () => {
    setFetchingCalendarTz(true);
    try {
      const response = await fetch(`${API}/oauth/calendar/timezone`, { credentials: 'include' });
      if (response.ok) {
        const data = await response.json();
        setCalendarSettings(prev => ({ ...prev, calendar_timezone: data.calendar_timezone }));
        toast.success(`Calendar timezone detected: ${data.calendar_timezone}`);
      } else {
        toast.error('Failed to fetch calendar timezone');
      }
    } catch (error) {
      console.error('Failed to fetch calendar timezone:', error);
      toast.error('Failed to fetch calendar timezone');
    } finally {
      setFetchingCalendarTz(false);
    }
  };

  // Auto-fetch calendar timezone when calendar is connected
  useEffect(() => {
    if (calendarStatus?.connected && calendarSettings && !calendarSettings.calendar_timezone) {
      fetchCalendarTimezone();
    }
  }, [calendarStatus?.connected, calendarSettings]);

  // Save profile settings
  const saveProfileSettings = async () => {
    if (!profileSettings) return;
    setSavingProfileSettings(true);
    try {
      const response = await fetch(`${API}/auth/profile/settings`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(profileSettings)
      });
      if (response.ok) {
        toast.success('Profile settings saved');
      } else {
        toast.error('Failed to save profile settings');
      }
    } catch (error) {
      console.error('Failed to save profile settings:', error);
      toast.error('Failed to save profile settings');
    } finally {
      setSavingProfileSettings(false);
    }
  };

  const connectCalendar = async () => {
    try {
      const response = await fetch(`${API}/oauth/calendar/connect`, { credentials: 'include' });
      if (response.ok) {
        const data = await response.json();
        if (data.authorization_url) {
          window.location.href = data.authorization_url;
        } else {
          toast.error('No authorization URL received');
        }
      } else {
        const err = await response.json().catch(() => ({}));
        toast.error(err.detail || 'Failed to start calendar connection');
      }
    } catch (e) { toast.error('Failed to start calendar connection'); }
  };

  const disconnectCalendar = async () => {
    try {
      const response = await fetch(`${API}/oauth/calendar/disconnect`, { method: 'POST', credentials: 'include' });
      if (response.ok) { toast.success('Calendar disconnected'); fetchData(); }
    } catch { toast.error('Failed to disconnect'); }
  };

  const saveCalendarSettings = async () => {
    if (!calendarSettings) return;
    setSavingCalSettings(true);
    try {
      const response = await fetch(`${API}/oauth/calendar/settings`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(calendarSettings)
      });
      if (response.ok) {
        toast.success('Calendar settings saved');
        setShowCalendarSettings(false);
      }
    } catch { toast.error('Failed to save settings'); }
    finally { setSavingCalSettings(false); }
  };

  const syncCalendarNow = async () => {
    setSyncing(true);
    setSyncResult(null);
    try {
      let requestBody;
      
      if (syncMode === 'dates') {
        // Use specific date range
        if (!syncStartDate || !syncEndDate) {
          toast.error('Please select both start and end dates');
          setSyncing(false);
          return;
        }
        requestBody = {
          start_date: syncStartDate,
          end_date: syncEndDate
        };
      } else {
        // Use days back/forward from today
        requestBody = {
          days_back: parseInt(syncDaysBack),
          days_forward: parseInt(syncDaysForward)
        };
      }
      
      const response = await fetch(`${API}/oauth/calendar/sync`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(requestBody)
      });
      
      if (response.ok) {
        const result = await response.json();
        setSyncResult(result);
        toast.success(`Synced ${result.synced_count} new events, updated ${result.updated_count} existing`);
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to sync calendar');
      }
    } catch (error) {
      console.error('Sync error:', error);
      toast.error('Failed to sync calendar');
    } finally {
      setSyncing(false);
    }
  };

  const openSyncDialog = () => {
    setSyncResult(null);
    setSyncMode('days');
    setSyncDaysBack('0');
    setSyncDaysForward('90');
    // Set default dates to today and 90 days forward
    const today = new Date().toISOString().split('T')[0];
    const future = new Date(Date.now() + 90 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];
    setSyncStartDate(today);
    setSyncEndDate(future);
    setShowSyncDialog(true);
  };

  const toggleCalendarSelection = (calId) => {
    if (!calendarSettings) return;
    const selected = calendarSettings.selected_calendars || [];
    const newSelected = selected.includes(calId)
      ? selected.filter(id => id !== calId)
      : [...selected, calId];
    setCalendarSettings({ ...calendarSettings, selected_calendars: newSelected });
  };

  // Gmail handlers
  const connectGmail = async () => {
    try {
      const response = await fetch(`${API}/oauth/gmail/connect`, { credentials: 'include' });
      if (response.ok) {
        const data = await response.json();
        window.location.href = data.authorization_url;
      } else {
        toast.error('Failed to start Gmail connection');
      }
    } catch { toast.error('Failed to start Gmail connection'); }
  };

  const disconnectGmail = async (email = null) => {
    try {
      const response = await fetch(`${API}/oauth/gmail/disconnect`, { 
        method: 'POST', 
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(email ? { email } : {})
      });
      if (response.ok) {
        toast.success(email ? `Gmail ${email} disconnected` : 'Gmail disconnected');
        setGmailStatus(null);
        fetchData();
      }
    } catch { toast.error('Failed to disconnect Gmail'); }
  };

  const syncGmailToSwitchboard = async () => {
    setGmailSyncing(true);
    try {
      const response = await fetch(`${API}/oauth/gmail/sync-to-switchboard`, { method: 'POST', credentials: 'include' });
      if (response.ok) {
        const result = await response.json();
        toast.success(`Synced ${result.synced_count} messages to Switchboard`);
      }
    } catch { toast.error('Failed to sync Gmail'); }
    finally { setGmailSyncing(false); }
  };

  const createOrganization = async () => {
    if (!orgName.trim()) { toast.error('Organization name required'); return; }
    try {
      const response = await fetch(`${API}/org/create`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ name: orgName })
      });
      if (response.ok) {
        toast.success('Organization created!');
        setShowCreateOrg(false);
        setOrgName('');
        fetchData();
      }
    } catch { toast.error('Failed to create organization'); }
  };

  const createDelegation = async () => {
    if (!delegationEmail.trim()) { toast.error('User email or ID required'); return; }
    if (!delegationProfileIds.length) { toast.error('Select at least one access profile'); return; }
    try {
      const response = await fetch(`${API}/org/delegations`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          from_user_id: user.user_id,
          to_user_id: delegationEmail.trim(),
          access_profile_ids: delegationProfileIds,
          delegation_scope_type: 'all_tasks',
        })
      });
      if (response.ok) {
        toast.success('Delegation created');
        setShowCreateDelegation(false);
        setDelegationEmail('');
        setDelegationProfileIds([]);
        fetchData();
      } else {
        const err = await response.json().catch(() => ({}));
        toast.error(err.detail || 'Failed to create delegation');
      }
    } catch { toast.error('Failed to create delegation'); }
  };

  const revokeDelegation = async (delegationId) => {
    try {
      const response = await fetch(`${API}/org/delegations/${delegationId}`, { method: 'DELETE', credentials: 'include' });
      if (response.ok) { toast.success('Delegation revoked'); fetchData(); }
    } catch { toast.error('Failed to revoke delegation'); }
  };

  const toggleProfileId = (profileId) => {
    setDelegationProfileIds(prev => prev.includes(profileId) ? prev.filter(s => s !== profileId) : [...prev, profileId]);
  };

  return (
    <PageLayout title="Settings" theme={theme} setTheme={setTheme}>
      <Tabs defaultValue="appearance" className="space-y-6">
        <TabsList className="grid w-full max-w-3xl grid-cols-5">
          <TabsTrigger value="appearance" data-testid="tab-appearance">
            <Palette className="h-4 w-4 mr-1" />Appearance
          </TabsTrigger>
          <TabsTrigger value="vocabulary" data-testid="tab-vocabulary">
            <SlidersHorizontal className="h-4 w-4 mr-1" />Vocabulary
          </TabsTrigger>
          <TabsTrigger value="integrations" data-testid="tab-integrations">
            <Link2 className="h-4 w-4 mr-1" />Integrations
          </TabsTrigger>
          <TabsTrigger value="subscription" data-testid="tab-subscription">
            <Zap className="h-4 w-4 mr-1" />Subscription
          </TabsTrigger>
          <TabsTrigger value="delegation" data-testid="tab-delegation">
            <Users className="h-4 w-4 mr-1" />Delegation
          </TabsTrigger>
        </TabsList>

        {/* Appearance Tab */}
        <TabsContent value="appearance" data-testid="appearance-tab-content">
          <Card className="max-w-3xl">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-base">
                <Palette className="w-4 h-4 text-violet-600" /> Appearance
              </CardTitle>
              <CardDescription>
                Choose how Orka looks and feels. Theme controls dark/light; density controls spacing and how task headers render.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div>
                <div className="flex items-center gap-1.5 text-xs uppercase tracking-wider text-muted-foreground font-semibold mb-2">
                  <Palette className="w-3 h-3" /> Theme
                </div>
                <div className="grid grid-cols-3 gap-2 max-w-md" data-testid="settings-theme-group">
                  {[
                    { key: 'light', label: 'Light', desc: 'Bright canvas', Icon: Sun },
                    { key: 'dark', label: 'Dark', desc: 'Low glare', Icon: Moon },
                    { key: 'system', label: 'System', desc: 'Match OS', Icon: Monitor },
                  ].map(({ key, label, desc, Icon }) => (
                    <button
                      key={key}
                      type="button"
                      onClick={() => setTheme(key)}
                      className={`flex flex-col items-start gap-1 p-3 rounded-xl border text-left transition-colors ${theme === key ? 'border-violet-500 bg-violet-50 dark:bg-violet-950/40' : 'border-border hover:bg-accent'}`}
                      data-testid={`settings-theme-${key}`}
                    >
                      <Icon className="w-4 h-4" />
                      <div className="text-sm font-semibold">{label}</div>
                      <div className="text-[10px] text-muted-foreground">{desc}</div>
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <div className="flex items-center gap-1.5 text-xs uppercase tracking-wider text-muted-foreground font-semibold mb-2">
                  <SlidersHorizontal className="w-3 h-3" /> Density
                </div>
                <div className="grid grid-cols-3 gap-2 max-w-md" data-testid="settings-density-group">
                  {[
                    { key: 'compact', label: 'Compact', desc: 'Dense layout, smaller paddings' },
                    { key: 'standard', label: 'Standard', desc: 'Balanced — the default' },
                    { key: 'calm', label: 'Calm', desc: 'Roomy; surface-bg task headers' },
                  ].map(({ key, label, desc }) => (
                    <button
                      key={key}
                      type="button"
                      onClick={() => setDensity(key)}
                      className={`flex flex-col items-start gap-1 p-3 rounded-xl border text-left transition-colors ${density === key ? 'border-violet-500 bg-violet-50 dark:bg-violet-950/40' : 'border-border hover:bg-accent'}`}
                      data-testid={`settings-density-${key}`}
                    >
                      <div className="text-sm font-semibold capitalize">{label}</div>
                      <div className="text-[10px] text-muted-foreground leading-snug">{desc}</div>
                    </button>
                  ))}
                </div>
                <p className="text-xs text-muted-foreground mt-2 max-w-md">
                  Calm density swaps the full-bleed gradient task header for a surface card with a coloured project edge,
                  and hides decorative category chips and zone-split footer for a quieter workspace.
                </p>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Vocabulary Tab — colour & label admin */}
        <TabsContent value="vocabulary" data-testid="vocabulary-tab-content">
          <VocabularySettingsPanel />
        </TabsContent>

        {/* Integrations Tab - Uniform Tiles */}
        <TabsContent value="integrations">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {/* Google Calendar Tile */}
            <Card className="flex flex-col" data-testid="integration-calendar">
              <CardHeader className="pb-3">
                <CardTitle className="flex items-center gap-3 text-base">
                  <div className="w-10 h-10 rounded-xl bg-white dark:bg-slate-100 border dark:border-slate-300 flex items-center justify-center flex-shrink-0">
                    <img src="https://cdn.simpleicons.org/googlecalendar" alt="Google Calendar" className="w-6 h-6" />
                  </div>
                  <div>
                    <span>Google Calendar</span>
                    <p className="text-xs text-muted-foreground font-normal mt-0.5">Sync calendar events as tasks</p>
                  </div>
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-0 flex-1 flex flex-col justify-end">
              {calendarStatus?.connected ? (
                <div className="space-y-2">
                  <div className="flex items-center justify-between p-2 bg-blue-50 dark:bg-blue-950/30 rounded-lg border border-blue-200 dark:border-blue-900">
                    <div className="flex items-center gap-2">
                      <div className="w-6 h-6 rounded-full bg-blue-100 dark:bg-blue-950/40 flex items-center justify-center">
                        <CheckCircle2 className="h-3 w-3 text-blue-600 dark:text-blue-400" />
                      </div>
                      <span className="text-sm font-medium">Connected</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <Button variant="ghost" size="icon" className="h-7 w-7" onClick={openSyncDialog} data-testid="sync-now-btn">
                        <RefreshCw className="h-3 w-3" />
                      </Button>
                      <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => setShowCalendarSettings(true)} data-testid="calendar-settings">
                        <SettingsIcon className="h-3 w-3" />
                      </Button>
                      <Button variant="ghost" size="icon" className="h-7 w-7 text-red-500" onClick={disconnectCalendar} data-testid="disconnect-calendar">
                        <Unlink className="h-3 w-3" />
                      </Button>
                    </div>
                  </div>
                  {calendarSettings && (
                    <p className="text-xs text-muted-foreground" data-testid="calendar-sync-summary">
                      Syncing {calendarSettings.selected_calendars?.length || 0} of your Google calendars
                      {' · '}
                      {calendarSettings.sync_direction === 'two_way' ? '2-way sync' : '1-way sync'}
                    </p>
                  )}
                </div>
              ) : (
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <XCircle className="h-4 w-4 text-muted-foreground" />
                    <p className="text-sm text-muted-foreground">Not connected</p>
                  </div>
                  <Button size="sm" onClick={connectCalendar} data-testid="connect-calendar">
                    <Link2 className="h-3 w-3 mr-1" />Connect
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>

            {/* LinkedIn Recruiter Tile */}
            <LinkedInIntegrationCard />

            {/* Gmail Tile */}
            <Card className="flex flex-col" data-testid="gmail-integration-card">
              <CardHeader className="pb-3">
                <CardTitle className="flex items-center gap-3 text-base">
                  <div className="w-10 h-10 rounded-xl bg-white dark:bg-slate-100 border dark:border-slate-300 flex items-center justify-center flex-shrink-0">
                    <img src="https://cdn.simpleicons.org/gmail" alt="Gmail" className="w-6 h-6" />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center justify-between">
                      <span>Gmail</span>
                      <Button size="sm" variant="outline" className="h-7 text-xs" onClick={connectGmail} data-testid="add-gmail-account">
                        <Plus className="h-3 w-3 mr-1" />Add
                      </Button>
                    </div>
                    <p className="text-xs text-muted-foreground font-normal mt-0.5">Multi-account email sync</p>
                  </div>
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-0 flex-1 flex flex-col justify-end">
              {gmailAccounts.length > 0 ? (
                <div className="space-y-2">
                  {/* List all connected accounts - compact */}
                  {gmailAccounts.map((account, idx) => (
                    <div key={account.email || idx} className="flex items-center justify-between p-2 bg-muted/50 rounded-lg border" data-testid={`gmail-account-${idx}`}>
                      <div className="flex items-center gap-2 min-w-0">
                        <div className="w-6 h-6 rounded-full bg-red-100 dark:bg-red-950/40 flex items-center justify-center flex-shrink-0">
                          <Mail className="h-3 w-3 text-red-500 dark:text-red-400" />
                        </div>
                        <span className="text-sm font-medium truncate">{account.email}</span>
                      </div>
                      <div className="flex items-center gap-1 flex-shrink-0">
                        <Button 
                          variant="ghost" 
                          size="icon"
                          className="h-7 w-7"
                          onClick={() => {
                            setGmailSyncing(true);
                            fetch(`${API}/oauth/gmail/sync-to-switchboard`, { 
                              method: 'POST', 
                              credentials: 'include',
                              headers: { 'Content-Type': 'application/json' },
                              body: JSON.stringify({ email: account.email })
                            })
                              .then(res => res.json())
                              .then(result => {
                                toast.success(`Synced ${result.synced_count || 0} messages`);
                              })
                              .catch(() => toast.error('Sync failed'))
                              .finally(() => setGmailSyncing(false));
                          }}
                          disabled={gmailSyncing}
                        >
                          {gmailSyncing ? (
                            <Loader2 className="h-3 w-3 animate-spin" />
                          ) : (
                            <RefreshCw className="h-3 w-3" />
                          )}
                        </Button>
                        <Button 
                          variant="ghost" 
                          size="icon"
                          className="h-7 w-7 text-red-500 hover:text-red-700 hover:bg-red-50 dark:text-red-400 dark:hover:text-red-300 dark:hover:bg-red-950/40"
                          onClick={() => disconnectGmail(account.email)}
                        >
                          <Trash2 className="h-3 w-3" />
                        </Button>
                      </div>
                    </div>
                  ))}
                  {/* Purge Gmail Data */}
                  <Button 
                    variant="outline" 
                    size="sm"
                    className="w-full mt-2 text-red-600 border-red-200 hover:bg-red-50 dark:text-red-400 dark:border-red-900 dark:hover:bg-red-950/40"
                    onClick={async () => {
                      if (!window.confirm('Purge all Gmail messages and threads? You can re-sync after.')) return;
                      try {
                        const res = await fetch(`${API}/api/switchboard/purge-provider/gmail`, { 
                          method: 'POST', 
                          credentials: 'include' 
                        });
                        if (res.ok) {
                          const result = await res.json();
                          toast.success(`Purged ${result.deleted_messages} messages, ${result.deleted_threads} threads`);
                        } else {
                          toast.error('Purge failed');
                        }
                      } catch { toast.error('Purge failed'); }
                    }}
                  >
                    <Trash2 className="h-3 w-3 mr-1" />Purge Gmail Data
                  </Button>
                </div>
              ) : (
                <div className="flex items-center justify-between">
                  <p className="text-sm text-muted-foreground">No accounts connected</p>
                </div>
              )}
            </CardContent>
          </Card>

            {/* WhatsApp Tile */}
            <WhatsAppIntegrationCard />

            {/* Slack Tile */}
            <SlackIntegrationCard />

            {/* Atlassian (Confluence + Jira) Tile */}
            <AtlassianIntegrationCard />

            {/* Google Drive (Drive + Docs + Sheets + Slides) Tile */}
            <GoogleDriveIntegrationCard />

            {/* YouTube Tile */}
            <YouTubeIntegrationCard />

            {/* OpenAI Integration */}
            <Card className="flex flex-col" data-testid="openai-integration-card">
              <CardHeader className="pb-3">
                <CardTitle className="flex items-center gap-3 text-base">
                  <div className="w-10 h-10 rounded-xl bg-white dark:bg-slate-100 border dark:border-slate-300 flex items-center justify-center flex-shrink-0">
                    <img src="https://cdn.simpleicons.org/openai" alt="OpenAI" className="w-6 h-6" />
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <span>OpenAI</span>
                      {openaiStatus?.ai_enabled ? (
                        <Badge variant="default" className="text-[10px] bg-emerald-500">Active</Badge>
                      ) : (
                        <Badge variant="outline" className="text-[10px]">Not Configured</Badge>
                      )}
                    </div>
                    <p className="text-xs text-muted-foreground font-normal mt-0.5">Powers Orka AI assistant</p>
                  </div>
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-0 flex-1 flex flex-col justify-end space-y-3">
                {openaiStatus?.has_key && !showOpenaiInput ? (
                  <div className="space-y-2">
                    <div className="flex items-center gap-2 p-2 bg-muted/50 rounded-lg">
                      <CheckCircle2 className="h-4 w-4 text-emerald-500 flex-shrink-0" />
                      <span className="text-xs font-mono">{openaiStatus.masked_key}</span>
                    </div>
                    <Button size="sm" variant="outline" className="w-full" onClick={() => setShowOpenaiInput(true)}
                      data-testid="update-openai-key-btn">
                      Update Key
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-2">
                    <div className="relative">
                      <Input
                        type="password"
                        value={openaiKey}
                        onChange={(e) => setOpenaiKey(e.target.value)}
                        placeholder="sk-proj-..."
                        className="font-mono text-xs h-8 pr-8"
                        data-testid="openai-key-input"
                      />
                    </div>
                    <div className="flex gap-1">
                      <Button size="sm" disabled={openaiSaving || !openaiKey.trim()}
                        data-testid="save-openai-key-btn"
                        onClick={async () => {
                          setOpenaiSaving(true);
                          try {
                            const res = await fetch(`${API}/ai/openai-key`, {
                              method: 'POST',
                              headers: { 'Content-Type': 'application/json' },
                              credentials: 'include',
                              body: JSON.stringify({ api_key: openaiKey })
                            });
                            if (res.ok) {
                              toast.success('OpenAI API key saved! AI features activated.');
                              setOpenaiKey('');
                              setShowOpenaiInput(false);
                              fetchData();
                            } else {
                              const err = await res.json().catch(() => ({}));
                              toast.error(err.detail || 'Failed to save key');
                            }
                          } catch(e) { toast.error('Error: ' + e.message); }
                          setOpenaiSaving(false);
                        }}>
                        {openaiSaving ? <Loader2 className="h-3 w-3 animate-spin" /> : 'Save'}
                      </Button>
                      {showOpenaiInput && openaiStatus?.has_key && (
                        <Button size="sm" variant="ghost" onClick={() => { setShowOpenaiInput(false); setOpenaiKey(''); }}>
                          Cancel
                        </Button>
                      )}
                    </div>
                    <p className="text-[10px] text-muted-foreground">
                      Get your key from <a href="https://platform.openai.com/api-keys" target="_blank" rel="noreferrer" className="text-violet-600 hover:underline">platform.openai.com/api-keys</a>
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* HubSpot Tile */}
            <Card className="flex flex-col opacity-60" data-testid="hubspot-integration-card">
              <CardHeader className="pb-3">
                <CardTitle className="flex items-center gap-3 text-base">
                  <div className="w-10 h-10 rounded-xl bg-white dark:bg-slate-100 border dark:border-slate-300 flex items-center justify-center flex-shrink-0">
                    <img src="https://cdn.simpleicons.org/hubspot" alt="HubSpot" className="w-6 h-6" />
                  </div>
                  <div>
                    <span>HubSpot</span>
                    <p className="text-xs text-muted-foreground font-normal mt-0.5">CRM and contacts sync</p>
                  </div>
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-0 flex-1 flex flex-col justify-end">
                <Badge variant="secondary">Coming Soon</Badge>
              </CardContent>
            </Card>

            {/* Telegram Tile */}
            <Card className="flex flex-col opacity-60" data-testid="telegram-integration-card">
              <CardHeader className="pb-3">
                <CardTitle className="flex items-center gap-3 text-base">
                  <div className="w-10 h-10 rounded-xl bg-white dark:bg-slate-100 border dark:border-slate-300 flex items-center justify-center flex-shrink-0">
                    <img src="https://cdn.simpleicons.org/telegram" alt="Telegram" className="w-6 h-6" />
                  </div>
                  <div>
                    <span>Telegram</span>
                    <p className="text-xs text-muted-foreground font-normal mt-0.5">Messaging sync</p>
                  </div>
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-0 flex-1 flex flex-col justify-end">
                <Badge variant="secondary">Coming Soon</Badge>
              </CardContent>
            </Card>

            {/* LINE Tile */}
            <Card className="flex flex-col opacity-60" data-testid="line-integration-card">
              <CardHeader className="pb-3">
                <CardTitle className="flex items-center gap-3 text-base">
                  <div className="w-10 h-10 rounded-xl bg-white dark:bg-slate-100 border dark:border-slate-300 flex items-center justify-center flex-shrink-0">
                    <img src="https://cdn.simpleicons.org/line" alt="LINE" className="w-6 h-6" />
                  </div>
                  <div>
                    <span>LINE</span>
                    <p className="text-xs text-muted-foreground font-normal mt-0.5">Messaging sync</p>
                  </div>
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-0 flex-1 flex flex-col justify-end">
                <Badge variant="secondary">Coming Soon</Badge>
              </CardContent>
            </Card>

            {/* Google Workspace Tile - in the grid */}
            <Card className="flex flex-col" data-testid="workspace-integration-card">
              <CardHeader className="pb-3">
                <CardTitle className="flex items-center gap-3 text-base">
                  <div className="w-10 h-10 rounded-xl bg-white dark:bg-slate-100 border dark:border-slate-300 flex items-center justify-center flex-shrink-0">
                    <img src="https://cdn.simpleicons.org/googleworkspace" alt="Google Workspace" className="w-6 h-6" />
                  </div>
                  <div>
                    <span>Google Workspace</span>
                    <p className="text-xs text-muted-foreground font-normal mt-0.5">Team directory sync</p>
                  </div>
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-0 flex-1 flex flex-col justify-end">
                <WorkspaceIntegrationTile />
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Subscription Tab */}
        <TabsContent value="subscription">
          <Card data-testid="subscription-card">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Zap className="w-5 h-5" />Subscription
              </CardTitle>
              <CardDescription>Manage your Orka subscription plan</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex flex-col items-center text-center py-12">
                <div className="w-16 h-16 rounded-full bg-cyan-50 dark:bg-cyan-950/40 flex items-center justify-center mb-4">
                  <Zap className="w-8 h-8 text-cyan-600 dark:text-cyan-400" />
                </div>
                <h3 className="text-lg font-semibold mb-2">Coming Soon</h3>
                <p className="text-sm text-muted-foreground max-w-md mb-4">
                  Subscription management is coming soon. You're currently on the <strong>Free Beta</strong> plan with full access to all features.
                </p>
                <Badge className="bg-cyan-100 text-cyan-700 border-cyan-200 dark:bg-cyan-950/40 dark:text-cyan-300 dark:border-cyan-900 text-sm px-4 py-1">Free Beta - Full Access</Badge>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Delegation Tab */}
        <TabsContent value="delegation">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Users className="w-5 h-5" />Delegation
              </CardTitle>
              <CardDescription>Manage who can act on your behalf</CardDescription>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-muted-foreground">Configure delegation settings in the Organisation page.</p>
              <Button variant="outline" size="sm" className="mt-3" onClick={() => window.location.href = '/organisation'}>
                Go to Organisation
              </Button>
            </CardContent>
          </Card>
        </TabsContent>

      </Tabs>

      {/* Calendar Settings Modal */}
      <Dialog open={showCalendarSettings} onOpenChange={setShowCalendarSettings}>
        <DialogContent className="sm:max-w-2xl max-h-[85vh] flex flex-col p-0" data-testid="calendar-settings-dialog">
          <DialogHeader className="px-6 pt-6 pb-3 border-b shrink-0">
            <DialogTitle>Calendar Sync Settings</DialogTitle>
            <DialogDescription>Configure how your calendar syncs with Orka.</DialogDescription>
          </DialogHeader>

          <div className="flex-1 overflow-y-auto px-6 py-4" data-testid="calendar-settings-scroll">
          {calendarSettings && (
            <div className="space-y-6 py-4">
              {/* Calendar Timezone - Read-only, from Google Calendar Settings API */}
              <div>
                <label className="text-sm font-medium mb-2 block flex items-center gap-2">
                  <Globe className="h-4 w-4" />
                  Google Calendar Primary Timezone
                </label>
                <div className="flex items-center gap-2">
                  <div className="flex-1 h-10 px-3 flex items-center bg-muted rounded-md text-sm font-medium">
                    {calendarSettings.calendar_timezone || 'Not detected'}
                  </div>
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={fetchCalendarTimezone}
                    disabled={fetchingCalendarTz}
                    data-testid="refresh-calendar-tz"
                  >
                    {fetchingCalendarTz ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <RefreshCw className="h-4 w-4" />
                    )}
                  </Button>
                </div>
                <p className="text-xs text-muted-foreground mt-1">
                  This is your PRIMARY timezone from Google Calendar settings. Events sync using this timezone.
                </p>
              </div>

              {/* Sync Direction */}
              <div>
                <label className="text-sm font-medium mb-2 block">Sync Direction</label>
                <Select value={calendarSettings.sync_direction} onValueChange={(v) => setCalendarSettings({ ...calendarSettings, sync_direction: v })}>
                  <SelectTrigger data-testid="sync-direction-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="one_way_import">
                      <div className="flex items-center gap-2">
                        <ArrowRight className="h-4 w-4" />
                        <span>One-way (Calendar → Orka)</span>
                      </div>
                    </SelectItem>
                    <SelectItem value="two_way">
                      <div className="flex items-center gap-2">
                        <ArrowLeftRight className="h-4 w-4" />
                        <span>Two-way sync</span>
                      </div>
                    </SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground mt-1">
                  {calendarSettings.sync_direction === 'two_way' 
                    ? 'Changes in Orka will update your calendar events' 
                    : 'Calendar events are imported to Orka (read-only)'}
                </p>
              </div>

              {/* Push Mode - only visible when two-way sync is enabled */}
              {calendarSettings.sync_direction === 'two_way' && (
              <div>
                <label className="text-sm font-medium mb-2 block">What to push to calendar</label>
                <Select value={calendarSettings.push_mode || 'meetings_only'} onValueChange={(v) => setCalendarSettings({ ...calendarSettings, push_mode: v })}>
                  <SelectTrigger data-testid="push-mode-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="meetings_only">
                      <span>Meetings only (default)</span>
                    </SelectItem>
                    <SelectItem value="all_planned">
                      <span>All planned tasks with time</span>
                    </SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground mt-1">
                  {(calendarSettings.push_mode || 'meetings_only') === 'meetings_only'
                    ? 'Only tasks marked as meetings will be pushed to your calendar'
                    : 'All tasks with a start time in Day Planner will appear as calendar events'}
                </p>
              </div>
              )}

              {/* Days Ahead */}
              <div>
                <label className="text-sm font-medium mb-2 block">Sync Days Ahead</label>
                <Select value={String(calendarSettings.sync_days_ahead || 7)} onValueChange={(v) => setCalendarSettings({ ...calendarSettings, sync_days_ahead: parseInt(v) })}>
                  <SelectTrigger data-testid="sync-days-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="3">3 days</SelectItem>
                    <SelectItem value="7">7 days</SelectItem>
                    <SelectItem value="14">14 days</SelectItem>
                    <SelectItem value="30">30 days</SelectItem>
                    <SelectItem value="60">60 days</SelectItem>
                    <SelectItem value="90">90 days</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Calendar Selection */}
              <div>
                <label className="text-sm font-medium mb-1 block">Which of your Google calendars to sync</label>
                <p className="text-xs text-muted-foreground mb-2">
                  These are the calendars in your Google account. Tick the ones you want Orka to mirror.
                </p>
                <div className="space-y-2 max-h-[180px] overflow-y-auto border border-border rounded-lg p-2">
                  {calendarList.length === 0 ? (
                    <p className="text-sm text-muted-foreground text-center py-4">Loading calendars...</p>
                  ) : (
                    calendarList.map((cal) => (
                      <div
                        key={cal.id}
                        className="flex items-center gap-3 p-2 hover:bg-muted/50 rounded cursor-pointer"
                        onClick={() => toggleCalendarSelection(cal.id)}
                        data-testid={`calendar-${cal.id}`}
                      >
                        <Checkbox checked={calendarSettings.selected_calendars?.includes(cal.id)} />
                        <div
                          className="w-3 h-3 rounded-full flex-shrink-0"
                          style={{ backgroundColor: cal.backgroundColor || '#4285f4' }}
                        />
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium truncate">{cal.summary}</p>
                          {cal.primary && <Badge variant="secondary" className="text-xs">Primary</Badge>}
                        </div>
                      </div>
                    ))
                  )}
                </div>
                <p className="text-xs text-muted-foreground mt-1" data-testid="calendar-selection-count">
                  {calendarSettings.selected_calendars?.length || 0} selected
                </p>
              </div>

              {/* Epic 7 — Sync Health panel */}
              <div className="space-y-2 pt-4 border-t" data-testid="sync-health-panel">
                <div className="flex items-center justify-between">
                  <div>
                    <label className="text-sm font-medium">Sync Health</label>
                    <p className="text-xs text-muted-foreground">
                      Recent calendar sync activity and reconciliation conflicts
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={fetchSyncLog}
                      disabled={loadingSyncLog}
                      data-testid="refresh-sync-log"
                    >
                      {loadingSyncLog ? <Loader2 className="h-3 w-3 animate-spin" /> : <RefreshCw className="h-3 w-3" />}
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleForceFullSync}
                      disabled={forcingFullSync}
                      data-testid="force-full-sync"
                      title="Ignore sync tokens and perform a full re-sync"
                    >
                      {forcingFullSync ? 'Resyncing…' : 'Force full resync'}
                    </Button>
                  </div>
                </div>

                {syncLog && (
                  <div className="flex items-center gap-3 text-xs" data-testid="sync-health-summary">
                    <span className="px-2 py-0.5 rounded bg-slate-100 dark:bg-slate-800 orka-text-secondary">
                      {syncLog.total} mapping{syncLog.total === 1 ? '' : 's'}
                    </span>
                    {syncLog.conflict_count > 0 ? (
                      <span className="px-2 py-0.5 rounded bg-red-100 dark:bg-red-950/40 text-red-700 dark:text-red-300 font-medium" data-testid="sync-conflict-count">
                        {syncLog.conflict_count} conflict{syncLog.conflict_count === 1 ? '' : 's'}
                      </span>
                    ) : (
                      <span className="px-2 py-0.5 rounded bg-emerald-100 dark:bg-emerald-950/40 text-emerald-700 dark:text-emerald-300">
                        no conflicts
                      </span>
                    )}
                  </div>
                )}

                <div className="max-h-40 overflow-y-auto border rounded-md divide-y divide-slate-100 dark:divide-slate-800" data-testid="sync-log-list">
                  {loadingSyncLog ? (
                    <div className="p-3 text-xs text-muted-foreground">Loading…</div>
                  ) : !syncLog || syncLog.entries.length === 0 ? (
                    <div className="p-3 text-xs text-muted-foreground">
                      No sync activity yet. Run a calendar sync to populate.
                    </div>
                  ) : (
                    syncLog.entries.map((entry) => {
                      const hasConflict = entry.sync_conflict;
                      const cancelled = entry.mapping_status === 'cancelled' || entry.calendar_sync_status === 'cancelled';
                      return (
                        <div
                          key={entry.mapping_id}
                          className="flex items-start gap-2 p-2 text-xs"
                          data-testid={`sync-log-entry-${entry.mapping_id}`}
                        >
                          <span
                            className={`mt-0.5 inline-block w-2 h-2 rounded-full flex-shrink-0 ${
                              hasConflict
                                ? 'bg-red-500'
                                : cancelled
                                ? 'bg-slate-400'
                                : 'bg-emerald-500'
                            }`}
                          />
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center justify-between gap-2">
                              <span className="font-medium truncate">
                                {entry.task_title || entry.external_id}
                              </span>
                              <span className="text-muted-foreground flex-shrink-0">
                                {entry.task_delivery_date || '—'}
                              </span>
                            </div>
                            <div className="text-muted-foreground flex items-center gap-2 mt-0.5">
                              <span>{entry.last_sync_result || 'unknown'}</span>
                              {entry.last_synced_at && (
                                <span>
                                  · {new Date(entry.last_synced_at).toLocaleString(undefined, {
                                    month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
                                  })}
                                </span>
                              )}
                            </div>
                            {hasConflict && entry.sync_conflict_fields?.length > 0 && (
                              <div className="mt-0.5 text-red-600 dark:text-red-400">
                                conflicts: {entry.sync_conflict_fields.join(', ')}
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })
                  )}
                </div>
              </div>
            </div>
          )}
          </div>

          <DialogFooter className="px-6 py-3 border-t shrink-0 bg-background">
            <Button variant="outline" onClick={() => setShowCalendarSettings(false)}>Cancel</Button>
            <Button onClick={saveCalendarSettings} disabled={savingCalSettings} data-testid="save-calendar-settings">
              {savingCalSettings ? 'Saving...' : 'Save Settings'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create Organization Modal */}
      <Dialog open={showCreateOrg} onOpenChange={setShowCreateOrg}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Create Organization</DialogTitle>
            <DialogDescription>Create an organization to collaborate with your team.</DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <label className="text-sm font-medium mb-1.5 block">Organization Name</label>
            <Input value={orgName} onChange={(e) => setOrgName(e.target.value)} placeholder="Acme Inc." data-testid="org-name-input" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateOrg(false)}>Cancel</Button>
            <Button onClick={createOrganization} data-testid="submit-create-org">Create</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create Delegation Modal */}
      <Dialog open={showCreateDelegation} onOpenChange={setShowCreateDelegation}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Create Delegation</DialogTitle>
            <DialogDescription>Grant someone access to act on your behalf.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <label className="text-sm font-medium mb-1.5 block">Delegate (User ID or Email)</label>
              <Input value={delegationEmail} onChange={(e) => setDelegationEmail(e.target.value)} placeholder="user@example.com" data-testid="delegation-email-input" />
            </div>
            <div>
              <label className="text-sm font-medium mb-2 block">Access Profile</label>
              <div className="space-y-2 max-h-60 overflow-y-auto">
                {accessProfiles.filter(p => !p.is_system || p.access_profile_id !== 'admin').map((profile) => (
                  <div key={profile.access_profile_id} className="flex items-center justify-between p-3 border border-border rounded-lg cursor-pointer hover:bg-muted/50" onClick={() => toggleProfileId(profile.access_profile_id)} data-testid={`profile-${profile.access_profile_id}`}>
                    <div>
                      <p className="font-medium text-sm">{profile.label || profile.profile_name || profile.access_profile_id}</p>
                      <p className="text-xs text-muted-foreground">{profile.description || ''}</p>
                    </div>
                    <Switch checked={delegationProfileIds.includes(profile.access_profile_id)} />
                  </div>
                ))}
                {accessProfiles.length === 0 && (
                  <p className="text-sm text-muted-foreground py-2">No access profiles available. Create profiles in the Organisation page.</p>
                )}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateDelegation(false)}>Cancel</Button>
            <Button onClick={createDelegation} data-testid="submit-delegation">Create Delegation</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Sync Now Dialog */}
      <Dialog open={showSyncDialog} onOpenChange={setShowSyncDialog}>
        <DialogContent className="max-w-2xl max-h-[85vh]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <RefreshCw className="h-5 w-5" />
              Sync Calendar Events
            </DialogTitle>
            <DialogDescription>
              Import events from your connected calendars into Orka as tasks.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-6 py-4">
            {/* Sync Mode Selection */}
            <div className="flex gap-2 p-1 bg-muted rounded-lg">
              <Button 
                variant={syncMode === 'days' ? 'default' : 'ghost'}
                className="flex-1"
                onClick={() => setSyncMode('days')}
                data-testid="sync-mode-days"
              >
                Relative (Days)
              </Button>
              <Button 
                variant={syncMode === 'dates' ? 'default' : 'ghost'}
                className="flex-1"
                onClick={() => setSyncMode('dates')}
                data-testid="sync-mode-dates"
              >
                Specific Dates
              </Button>
            </div>

            {/* Days Mode */}
            {syncMode === 'days' && (
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium mb-2 block">Days Back (Past)</label>
                  <Select value={syncDaysBack} onValueChange={setSyncDaysBack}>
                    <SelectTrigger data-testid="sync-days-back">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="0">Today only</SelectItem>
                      <SelectItem value="7">7 days</SelectItem>
                      <SelectItem value="14">14 days</SelectItem>
                      <SelectItem value="30">30 days</SelectItem>
                      <SelectItem value="60">60 days</SelectItem>
                      <SelectItem value="90">90 days</SelectItem>
                      <SelectItem value="120">120 days</SelectItem>
                      <SelectItem value="150">150 days</SelectItem>
                      <SelectItem value="180">180 days (max)</SelectItem>
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-muted-foreground mt-1">How far back from today</p>
                </div>
                <div>
                  <label className="text-sm font-medium mb-2 block">Days Forward (Future)</label>
                  <Select value={syncDaysForward} onValueChange={setSyncDaysForward}>
                    <SelectTrigger data-testid="sync-days-forward">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="7">7 days</SelectItem>
                      <SelectItem value="14">14 days</SelectItem>
                      <SelectItem value="30">30 days</SelectItem>
                      <SelectItem value="60">60 days</SelectItem>
                      <SelectItem value="90">90 days (default)</SelectItem>
                      <SelectItem value="120">120 days</SelectItem>
                      <SelectItem value="180">180 days</SelectItem>
                      <SelectItem value="365">365 days (1 year)</SelectItem>
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-muted-foreground mt-1">How far ahead from today</p>
                </div>
              </div>
            )}

            {/* Dates Mode */}
            {syncMode === 'dates' && (
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium mb-2 block">Start Date</label>
                  <Input
                    type="date"
                    value={syncStartDate}
                    onChange={(e) => setSyncStartDate(e.target.value)}
                    className="font-mono"
                    data-testid="sync-start-date"
                  />
                  <p className="text-xs text-muted-foreground mt-1">Beginning of date range</p>
                </div>
                <div>
                  <label className="text-sm font-medium mb-2 block">End Date</label>
                  <Input
                    type="date"
                    value={syncEndDate}
                    onChange={(e) => setSyncEndDate(e.target.value)}
                    className="font-mono"
                    data-testid="sync-end-date"
                  />
                  <p className="text-xs text-muted-foreground mt-1">End of date range</p>
                </div>
              </div>
            )}

            {/* Sync Button */}
            <div className="flex justify-center">
              <Button 
                onClick={syncCalendarNow} 
                disabled={syncing} 
                className="w-full max-w-xs"
                data-testid="execute-sync-btn"
              >
                {syncing ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Syncing...
                  </>
                ) : (
                  <>
                    <RefreshCw className="h-4 w-4 mr-2" />
                    Sync Now
                  </>
                )}
              </Button>
            </div>

            {/* Sync Results */}
            {syncResult && (
              <div className="space-y-4">
                {/* Summary */}
                <div className="bg-muted/50 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="font-medium">Sync Complete</h4>
                    <Badge variant="secondary">
                      {syncResult.total_events} events found
                    </Badge>
                  </div>
                  <div className="grid grid-cols-3 gap-4 text-sm">
                    <div className="text-center p-2 bg-background rounded">
                      <p className="text-2xl font-bold text-emerald-600 dark:text-emerald-400">{syncResult.synced_count}</p>
                      <p className="text-muted-foreground">New Events</p>
                    </div>
                    <div className="text-center p-2 bg-background rounded">
                      <p className="text-2xl font-bold text-blue-600 dark:text-blue-400">{syncResult.updated_count}</p>
                      <p className="text-muted-foreground">Updated</p>
                    </div>
                    <div className="text-center p-2 bg-background rounded">
                      <p className="text-2xl font-bold">{syncResult.calendars_synced}</p>
                      <p className="text-muted-foreground">Calendars</p>
                    </div>
                  </div>
                  <p className="text-xs text-muted-foreground mt-2 text-center">
                    Date range: {syncResult.date_range?.from} to {syncResult.date_range?.to}
                  </p>
                </div>

                {/* Events List */}
                {syncResult.events && syncResult.events.length > 0 && (
                  <div>
                    <h4 className="font-medium mb-2 flex items-center gap-2">
                      <CalendarDays className="h-4 w-4" />
                      Events Found ({syncResult.events.length})
                    </h4>
                    <ScrollArea className="h-[300px] border border-border rounded-lg">
                      <div className="p-2 space-y-1">
                        {syncResult.events.map((event, idx) => (
                          <div 
                            key={event.event_id || idx}
                            className={`flex items-center gap-3 p-3 rounded-lg ${
                              event.is_new ? 'bg-emerald-500/10 border border-emerald-500/20' : 'bg-muted/30'
                            }`}
                            data-testid={`sync-event-${idx}`}
                          >
                            <div className="flex-shrink-0">
                              {event.is_new ? (
                                <Badge variant="default" className="bg-emerald-600 text-xs">NEW</Badge>
                              ) : (
                                <Badge variant="secondary" className="text-xs">Synced</Badge>
                              )}
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="font-medium text-sm truncate">{event.title}</p>
                              <div className="flex items-center gap-2 text-xs text-muted-foreground">
                                <CalendarDays className="h-3 w-3" />
                                <span>{event.date}</span>
                                {event.start_time && (
                                  <>
                                    <Clock className="h-3 w-3 ml-2" />
                                    <span>{event.start_time}{event.end_time && ` - ${event.end_time}`}</span>
                                  </>
                                )}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </ScrollArea>
                  </div>
                )}
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowSyncDialog(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </PageLayout>
  );
};

export default Settings;
